"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { track } from "@lib/track";

type Props = {
  /** Google Maps `/maps/embed?pb=…` URL. */
  embedSrc: string;
  /** Where the poster links to when JS is off, or when the embed fails. */
  mapsHref: string;
  /** Accessible name for the iframe. */
  title: string;
  /** The static map. Intrinsic dimensions set the container's aspect ratio. */
  poster: { src: string; width: number; height: number; alt: string };
  /** `sizes` for the poster — same value the plain <Image> would have used. */
  sizes: string;
  /**
   * Analytics label. The page path, so these events join `page_view` on the
   * same key — same convention as the /contactus form's events.
   */
  trackLabel: string;
  className?: string;
};

/**
 * Nothing reaches Google until the visitor clicks. The embed pulls ~900KB of
 * Maps JS and sets Google cookies on load, so it is not something to spend on
 * every visitor to a page whose map is decoration for an address written in
 * text beside it.
 */
const LOAD_TIMEOUT_MS = 8000;

const COMPONENT = "MapEmbed";

/** Fraction of the map that must be on screen to count as seen. */
const VIEW_THRESHOLD = 0.5;

function send(event: string, label: string, meta?: Record<string, unknown>) {
  try {
    track(
      event,
      { label, component: COMPONENT },
      { event, label, component: COMPONENT, ...(meta ? { meta } : {}) },
    );
  } catch {}
}

export default function MapEmbed({
  embedSrc,
  mapsHref,
  title,
  poster,
  sizes,
  trackLabel,
  className = "",
}: Props) {
  const [state, setState] = useState<"poster" | "loading" | "loaded" | "failed">(
    "poster",
  );
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frame = useRef<HTMLDivElement | null>(null);

  // Clicked-at, so map_embed_loaded can carry how long Google actually took —
  // the number that justifies or kills the click-to-load decision.
  const openedAt = useRef<number | null>(null);

  // Exactly one outcome event per open. The timeout and the load event race
  // each other on a slow connection: the timeout can fire `blocked`, and
  // Google can then turn up and fire `loaded` after it. Without this guard
  // that open would be counted twice, once in each column.
  const outcomeSent = useRef(false);

  const sendOutcome = useCallback(
    (event: string, meta?: Record<string, unknown>) => {
      if (outcomeSent.current) return;
      outcomeSent.current = true;
      send(event, trackLabel, meta);
    },
    [trackLabel],
  );

  // page_view alone can't be the denominator for a click on something most of
  // the way down a long page — it counts people who never scrolled to the map.
  // This is the "reached it" step, and it fires once per mount.
  useEffect(() => {
    const el = frame.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.intersectionRatio >= VIEW_THRESHOLD) {
            send("map_embed_view", trackLabel);
            observer.disconnect();
            return;
          }
        }
      },
      { threshold: VIEW_THRESHOLD },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [trackLabel]);

  // A cross-origin iframe gives no usable failure signal — `onError` doesn't
  // fire for a frame blocked by an extension, a content blocker or a strict
  // cookie policy, and the document inside is opaque to us. A timeout is the
  // only thing that can tell "slow" from "never coming", so that is what drops
  // us back to the poster instead of leaving a blank rectangle.
  useEffect(() => {
    if (state !== "loading") return;
    timer.current = setTimeout(() => {
      setState("failed");
      sendOutcome("map_embed_blocked", { reason: "timeout", afterMs: LOAD_TIMEOUT_MS });
    }, LOAD_TIMEOUT_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [state, sendOutcome]);

  // A frame the browser refused — blocked by CSP, by an extension, or by
  // X-Frame-Options — is left holding about:blank, and Chrome fires `load` for
  // it exactly as if it had worked. So `load` alone can't be trusted: check
  // that the document inside is genuinely cross-origin. Reading
  // contentWindow.location throws a SecurityError when Google really is in
  // there, and returns a readable "about:blank" when nothing is. Firefox tends
  // to fire no event at all in this case, which the timeout already covers.
  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLIFrameElement>) => {
      let refused: boolean;
      try {
        refused = e.currentTarget.contentWindow?.location?.href !== undefined;
      } catch {
        refused = false;
      }
      const ms = openedAt.current ? Date.now() - openedAt.current : undefined;
      if (refused) {
        setState("failed");
        sendOutcome("map_embed_blocked", { reason: "refused", ms });
      } else {
        setState("loaded");
        sendOutcome("map_embed_loaded", ms === undefined ? undefined : { ms });
      }
    },
    [sendOutcome],
  );

  const showPoster = state !== "loaded";
  const showOverlay = state === "poster" || state === "failed";

  return (
    <div
      ref={frame}
      className={`relative overflow-hidden rounded-2xl shadow-lg ${className}`}
      style={{ aspectRatio: `${poster.width} / ${poster.height}` }}
    >
      {/* The poster is always rendered until the embed is actually up: it is
          the server-rendered content, the thing behind a loading iframe, and
          where a failure lands. Matching aspect ratios mean the swap in either
          direction shifts nothing. */}
      {showPoster && (
        <Image
          src={poster.src}
          alt={poster.alt}
          fill
          sizes={sizes}
          className="object-cover"
        />
      )}

      {(state === "loading" || state === "loaded") && (
        <iframe
          src={embedSrc}
          title={title}
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleLoad}
          className="absolute inset-0 h-full w-full border-0"
        />
      )}

      {showOverlay && (
        <a
          href={mapsHref}
          target="_blank"
          rel="noopener noreferrer"
          // With JS on and nothing broken yet, the click loads the embed in
          // place. Once we've given up on Google — or if JS never ran — this
          // is a plain link to Google Maps, which is the honest fallback.
          onClick={(e) => {
            if (state === "failed") {
              // Leaving for Google Maps because our embed didn't work. Sent as
              // its own event rather than another map_embed_open: it is an exit
              // to a third party, not an attempt to load the map here.
              send("map_embed_fallback_click", trackLabel);
              return;
            }
            e.preventDefault();
            openedAt.current = Date.now();
            // Attempt, not outcome — named for intent and fired before the
            // frame resolves. The outcome is map_embed_loaded or
            // map_embed_blocked, exactly one of which follows.
            send("map_embed_open", trackLabel);
            setState("loading");
          }}
          className="absolute inset-0 flex flex-col items-center justify-end gap-2 bg-black/10 p-5 text-center transition-colors hover:bg-black/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ outlineColor: "var(--accent)" }}
        >
          <span className="rounded-full bg-white/95 px-5 py-2 text-[0.85rem] font-medium text-[var(--ink)] shadow-md">
            {state === "failed"
              ? "Open in Google Maps"
              : "Show interactive map"}
          </span>
          {state === "failed" && (
            <span className="rounded-full bg-black/60 px-3 py-1 text-[0.72rem] text-white">
              The map couldn&apos;t load here
            </span>
          )}
        </a>
      )}

      {state === "loading" && (
        <span
          aria-live="polite"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-[0.72rem] text-white"
        >
          Loading map…
        </span>
      )}
    </div>
  );
}
